import { debounce } from "@/utils/Debounce";
import { computed, type ComputedRef } from "vue";
import { useRoute, useRouter } from "vue-router";

export const useUrlSearch = (listenOff: boolean) => {
  const route = useRoute();
  const router = useRouter();

  const searchValue = computed(() =>
    !listenOff ? (route.query.search?.toString() ?? null) : null
  );

  const handleSearch = debounce(() => {
    router.push({ query: { search: searchValue.value } });
  });

  return {
    searchValue,
    handleSearch,
  };
};

type TQueryConstructor = StringConstructor | NumberConstructor | BooleanConstructor;

type TQueryValue<T extends TQueryConstructor> = T extends StringConstructor
  ? string
  : T extends NumberConstructor
    ? number
    : boolean;

export const useUrlQuery = <T extends Record<string, TQueryConstructor>>(schema: T) => {
  const route = useRoute();

  return Object.fromEntries(
    Object.entries(schema).map(([key, Constructor]) => [
      key,
      computed(() => {
        const val = route.query[key];
        if (val == null) return undefined;
        return Constructor(val.toString()) as TQueryValue<typeof Constructor>;
      }),
    ])
  ) as { [K in keyof T]: ComputedRef<TQueryValue<T[K]> | undefined> };
};

export const useUrlParams = <T extends Record<string, TQueryConstructor>>(schema: T) => {
  const route = useRoute();

  return Object.fromEntries(
    Object.entries(schema).map(([key, Constructor]) => [
      key,
      computed(() => {
        const val = (route.params as Record<string, string | string[]>)[key];
        if (val == null) return null;
        const str = Array.isArray(val) ? val[0] : val;
        if (str == null) return null;
        return Constructor(str.toString()) as TQueryValue<typeof Constructor>;
      }),
    ])
  ) as { [K in keyof T]: ComputedRef<TQueryValue<T[K]> | null> };
};
